// ref. https://github.com/microcmsio/react-hooks-use-modal/blob/5c7e3f90dd01c1135dec87915e40669e6e8f7251/src/index.tsx
import {
  type CSSProperties,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { createPortal } from 'react-dom'
import { lockScroll, unlockScroll } from '~/lib/scrollLocker'
import { useBinary } from '../useBinary'
import { useModalKeyHandler } from './useModalKeyHandler'

export type ModalStyleProps = {
  style?: CSSProperties
  className?: string
  wrapperStyle?: CSSProperties
  overlayStyle?: CSSProperties
  wrapperClassName?: string
  overlayClassName?: string
}

export type ModalProps = ModalStyleProps & {
  isOpen: boolean
  close: () => void
  onOverlayClick: MouseEventHandler<HTMLDivElement>
  elementId: string
  children?: ReactNode
}

export type ModalOptions = {
  preventScroll?: boolean
  closeOnOverlayClick?: boolean
}

export type UseModal = (
  elementId?: string,
  options?: ModalOptions
) => readonly [
  ModalWrapper: React.FC<Partial<ModalProps>>,
  open: () => void,
  close: () => void,
  isOpen: boolean,
]

const Modal: React.FC<ModalProps> = ({
  children,
  isOpen = false,
  close,
  onOverlayClick,
  elementId,
  style,
  className = '',
  wrapperStyle,
  overlayStyle,
  wrapperClassName = '',
  overlayClassName = '',
}) => {
  const ref = useRef<HTMLDivElement>(null)
  useModalKeyHandler(isOpen, close, ref)

  if (isOpen === false) return null
  const element = document.getElementById(elementId)
  if (!element) throw new Error('指定されたelementIdに対する要素がありません')

  return createPortal(
    <div
      role="dialog"
      aria-modal
      className={`fixed inset-0 z-modal-base flex items-center justify-center ${wrapperClassName}`}
      style={wrapperStyle}
    >
      <div
        className={`fixed inset-0 z-10 ${overlayClassName}`}
        style={overlayStyle}
        onClick={onOverlayClick}
        aria-hidden
      />
      <div ref={ref} className={`z-20 ${className}`} style={style}>
        {children}
      </div>
    </div>,
    element
  )
}

export const useModal: UseModal = (elementId = '__next', options = {}) => {
  const { preventScroll = true, closeOnOverlayClick = true } = options
  const [isOpen, open, close] = useBinary(false)

  useEffect(() => {
    if (!preventScroll) return
    if (isOpen) lockScroll()
    else unlockScroll()
  }, [isOpen, preventScroll])

  const onOverlayClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
      if (closeOnOverlayClick) close()
    },
    [closeOnOverlayClick, close]
  )

  const ModalWrapper: React.FC<Partial<ModalProps>> = useCallback(
    (props) => (
      <Modal
        isOpen={isOpen}
        close={close}
        onOverlayClick={onOverlayClick}
        elementId={elementId}
        {...props}
      />
    ),
    [isOpen, close, onOverlayClick, elementId]
  )

  return [ModalWrapper, open, close, isOpen]
}
